<template>
  <div class="login-page">
    <div class="login-visual" aria-hidden="true" />
    <main class="login-panel">
      <img class="right-one" src="/static/img/right-one.png" alt="" aria-hidden="true" />
      <img class="right-two" src="/static/img/right-two.png" alt="" aria-hidden="true" />
      <img class="right-three" src="/static/img/right-three.png" alt="" aria-hidden="true" />
      <img class="right-four" src="/static/img/right-four.png" alt="" aria-hidden="true" />

      <div class="login-form">
        <section class="login-card">
          <div class="test-badge"><span>测试版</span></div>
          <img class="login-logo" src="/static/img/logo-img.png" alt="IVUSmart" />
          <p class="login-subtitle">Clinical and imaging data management systems</p>

          <el-form ref="formRef" :model="form" :rules="rules" @submit.prevent="submit">
            <div v-if="licenseWarning" class="license-warning" role="alert">
              <el-icon><InfoFilled /></el-icon>
              <span>{{ licenseWarning }}</span>
            </div>
            <el-form-item prop="userName">
              <el-input
                v-model.trim="form.userName"
                size="large"
                placeholder="请输入您的用户名"
                autocomplete="username"
                show-word-limit
                maxlength="32"
              >
                <template #prefix>
                  <img class="field-icon" src="/static/img/user.png" alt="" aria-hidden="true" />
                </template>
              </el-input>
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model.trim="form.password"
                size="large"
                :type="passwordVisible ? 'text' : 'password'"
                placeholder="请输入您的登录密码"
                autocomplete="current-password"
                minlength="8"
                maxlength="18"
                @keyup.enter="submit"
              >
                <template #prefix>
                  <img class="field-icon" src="/static/img/psword.png" alt="" aria-hidden="true" />
                </template>
                <template #suffix>
                  <button
                    class="password-toggle"
                    type="button"
                    :aria-label="passwordVisible ? '隐藏密码' : '显示密码'"
                    @click="passwordVisible = !passwordVisible"
                  >
                    <el-icon v-if="passwordVisible"><View /></el-icon>
                    <img v-else src="/static/img/EyeSlash.png" alt="" aria-hidden="true" />
                  </button>
                </template>
              </el-input>
            </el-form-item>
            <el-button class="login-button" type="primary" native-type="submit" size="large" :loading="loading">
              登录
            </el-button>
          </el-form>

          <button class="license-link" type="button" @click="openLicensePicker">导入license</button>
          <div class="login-notice">
            <el-icon><InfoFilled /></el-icon>
            <span
              >本工具不用于疾病诊断，也不用于治愈、缓解、治疗或预防疾病。仅供学术研究目的进行的数据及影像管理，账号仅医务人员使用，请切勿将账号密码移交给他人。</span
            >
          </div>
        </section>
      </div>

      <footer class="login-footer">© 2026 Beyondsoft Corporation. All Rights Reserved.</footer>
      <input ref="licenseInput" class="license-input" type="file" @change="licenseFileChange" />
    </main>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'
import { InfoFilled, View } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { fetchMenu, getAuthSecret, getLicenseDays, importLicense as uploadLicense, login } from '@/services/auth/auth'
import { getUser } from '@/services/system/system'
import { useAuthStore } from '@/stores/auth'
import { useRouteStore } from '@/stores/route'
import { ApiError } from '@/core/errors'
import {
  isApiResult,
  resetHttpSessionState,
  resolveApiMessageType,
  type ApiMessageType,
  type ApiResult
} from '@/core/http/client'
import { encryptWithSm2PublicKey } from '@/core/crypto/sm2'
import { clearAuthorizedRoutes, installAuthorizedRoutes } from '@/router/authorized'
import { collectMenuPermissions } from '@/router/catalog'

const router = useRouter()
const auth = useAuthStore()
const routeStore = useRouteStore()
const formRef = ref<FormInstance>()
const licenseInput = ref<HTMLInputElement>()
const loading = ref(false)
const passwordVisible = ref(false)
const licenseWarning = ref('')
const form = reactive({ userName: '', password: '' })
const rules: FormRules = {
  userName: [{ required: true, message: '请输入您的用户名', trigger: 'blur' }],
  password: [
    { required: true, message: '请输入您的登录密码', trigger: 'blur' },
    { min: 8, max: 18, message: '密码长度为 8-18 位', trigger: 'blur' }
  ]
}

onMounted(() => {
  void loadLicenseDays()
})

async function loadLicenseDays() {
  try {
    const result = await getLicenseDays()
    if (!result.flag) return
    const days = result.days ?? 0
    const expiryTip =
      days > 0
        ? `系统中License的有效期距离当前日期已不足${days}天`
        : `您的license已过期`
    licenseWarning.value = `${expiryTip}，更新license请联系销售或拨打400-****联系运维人员或发送邮件至***.com邮箱`
  } catch {
    // License status is optional and does not block login.
  }
}

function openLicensePicker() {
  licenseInput.value?.click()
}

async function licenseFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return

  const payload = new FormData()
  payload.append('file', file)
  try {
    const result = await uploadLicense(payload)
    showResultMessage(result, 'license导入成功')
    window.setTimeout(() => window.location.reload(), 1000)
  } catch (error) {
    showRequestError(error, 'license导入失败，请稍后重试')
  }
}

async function submit() {
  if (loading.value) return
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) return
  loading.value = true
  try {
    const secret = await getAuthSecret()
    const payload = JSON.stringify({ username: form.userName, password: form.password })
    const encryptedPayload = encryptWithSm2PublicKey(payload, secret.publicKey)
    const response = await login(encryptedPayload, secret.flag)
    if (!response.tokenValue) throw new Error('登录响应缺少 token')

    auth.setAccessToken(response.tokenValue, response.tokenName, response.needModify, response.version)
    resetHttpSessionState()

    const menus = await fetchMenu()
    if (!Array.isArray(menus) || menus.length === 0) {
      auth.clearSession()
      routeStore.clear()
      clearAuthorizedRoutes()
      showMessage('此账号没有系统权限，请找管理员开通', 'error')
      return
    }

    const userInfo = await getUser(response.userId)
    const organization = userInfo?.organization

    const menuPermissions = collectMenuPermissions(menus)
    const permissions = response.roles.some((role) => role.isSuperAdmin)
      ? [...new Set(['*:*:*', ...menuPermissions])]
      : menuPermissions
    const user = {
      userId: response.userId,
      userName: userInfo?.userName || userInfo?.username || response.username,
      orgName: userInfo?.orgName || organization?.orgName,
      orgId: userInfo?.orgId ?? organization?.orgId,
      picturePath: userInfo?.picturePath || organization?.picturePath,
      organization,
      roles: response.roles,
      permissions
    }
    auth.setSession(response.tokenValue, user, menus)
    routeStore.hydrate(menus)
    installAuthorizedRoutes(router, menus)

    await router.replace(routeStore.firstPath || '/login')
  } catch (error) {
    auth.clearSession()
    routeStore.clear()
    clearAuthorizedRoutes()
    showRequestError(error, '登录失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

function showMessage(message: string | undefined, type: ApiMessageType) {
  const normalizedMessage = message?.trim()
  if (!normalizedMessage) return
  if (type === 'success') ElMessage.success(normalizedMessage)
  else if (type === 'warning') ElMessage.warning(normalizedMessage)
  else ElMessage.error(normalizedMessage)
}

function showResultMessage(result: ApiResult<unknown>, fallbackMessage?: string) {
  showMessage(result.msg || fallbackMessage, resolveApiMessageType(result))
}

function showRequestError(error: unknown, fallbackMessage: string) {
  if (error instanceof ApiError) {
    const result = isApiResult<unknown>(error.details) ? error.details : undefined
    showMessage(error.message || fallbackMessage, result ? resolveApiMessageType(result) : 'error')
    return
  }
  showMessage(fallbackMessage, 'error')
}
</script>

<style scoped>
.login-page {
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: #f1f2f9;
}

.login-visual {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  background: url('/static/img/login.png') right / cover no-repeat;
}

.login-panel {
  position: relative;
  flex: 0 0 600px;
  width: 600px;
  min-width: 600px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.login-form {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 0;
  padding: 0 100px 100px;
}

.login-card {
  width: 100%;
}

.test-badge {
  padding: 0 20px 10px 0;
  text-align: right;
}

.test-badge span {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 2px;
  color: #fff;
  background: #ed7b2f;
  font-size: 14px;
}

.login-logo {
  display: block;
  width: 313px;
  max-width: 100%;
  height: 69px;
  object-fit: contain;
  object-position: left center;
}

.login-subtitle {
  margin: 4px 0 40px;
  font-size: 16px;
  font-weight: 200;
  line-height: 22px;
}

.license-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 12px;
  color: #e6a23c;
  font-size: 13px;
  line-height: 20px;
}

.license-warning .el-icon {
  flex: 0 0 auto;
  margin-top: 3px;
}

.field-icon {
  display: block;
  width: 24px;
  height: 24px;
}

:deep(.el-input__prefix) {
  display: flex;
  align-items: center;
  padding-left: 1px;
}

.password-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  color: #888;
  background: transparent;
  cursor: pointer;
}

.password-toggle .el-icon {
  font-size: 22px;
}

.password-toggle img {
  width: 20px;
  height: 20px;
}

.login-button {
  display: block;
  width: 100%;
}

.license-link {
  display: block;
  width: 100%;
  margin-top: 10px;
  padding: 0;
  border: 0;
  color: #0052d9;
  background: transparent;
  font: inherit;
  font-size: 14px;
  text-align: right;
  cursor: pointer;
}

.license-link:hover {
  text-decoration: underline;
}

.login-notice {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin-top: 16px;
  color: #f00;
  font-size: 14px;
  line-height: 20px;
}

.login-notice .el-icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: #f00;
}

.login-footer {
  position: absolute;
  right: 0;
  bottom: 40px;
  left: 0;
  z-index: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 200;
}

.license-input {
  display: none;
}

.right-one,
.right-two,
.right-three,
.right-four {
  position: absolute;
  z-index: 0;
  pointer-events: none;
}

.right-one {
  top: 0;
  left: 0;
}

.right-two {
  top: 50px;
  right: 210px;
}

.right-three {
  top: 60px;
  right: 10px;
}

.right-four {
  bottom: 0;
  left: 25%;
}
</style>
